/**
 * TalkrouteAPIService.js
 * Talkroute
 *
 * Created by Vel Genov on 6/12/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import os from "os";

import _ from "lodash";

import FilePathHelper from "@helpers/filePathHelper";
import validateArray from "@helpers/arrayValidator";

import getApiConstants from "@constants/apiConstants";
import getWebRTCConstants from "@constants/webRTCConstants";

import appConfig from "../config/index";

import { callHistoryJSONParser } from "./dataClasses/CallHistoryData";
import { voicemailsJSONParser } from "./dataClasses/VoicemailData";
import { conversationJSONParser } from "./dataClasses/ConversationData";
import { textMessageJSONParser } from "./dataClasses/TextMessageData";
import uiString from "../constants/uiString";
import { voicemailMailboxJSONParser } from "./dataClasses/VoicemailMailbox";
import { phoneNumberJSONParser } from "./dataClasses/TalkroutePhoneNumber";
import UserPermissionsData from "./dataClasses/UserPermissionsData";
import { getExtensionFromSignature } from "@utils";
import { IConversationV1 } from "@models/Conversation.models";
import { RootState } from "@helpers/store/store";

/**
 * A singleton class that manages calls to the Talkroute API service
 */
export class TalkrouteAPIService {
  interceptor: any;
  constructor() {
    this.interceptor = null;
  }

  // #region Authentication

  /**
   * Sets the user authentication token in the API service
   */
  setAPIToken = (token, refreshToken) => {
    if (token) {
      appConfig.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      // Response interceptor for API calls);
    }
  };

  /**
   * Sets up the interceptor for impersonation
   */
  setupRequestInterceptor = (getState: () => RootState) => {
    // remove any existing interceptor
    if (this.interceptor !== null) {
      appConfig.axios.interceptors.request.eject(this.interceptor);
    }
  
    // add a new interceptor
    this.interceptor = appConfig.axios.interceptors.request.use(
      (config) => {
        const impersonateUserId = getState().impersonateUserReducer.impersonateUserId;
  
        // if impersonateUserId is there, set the Impersonate header
        if (impersonateUserId) {
          config.headers["Impersonate"] = impersonateUserId.toString();
        }
  
        return config;
      },
      (error) => {
        console.log('Interceptor error:', error);
        return Promise.reject(error);
      }
    );
  };

  /**
   * Requests a user login
   *
   * @param user the user name
   * @param password the password
   * @returns a promise that can either succseed or fail
   * @returns a successfull promise returns the auhtnetication token
   * @returns a failed promise returns an error message
   */
  authenticate = (user, password) => {
    const deviceId = appConfig.machineId;

    const dataPromise = new Promise((resolve, reject) => {
      appConfig.axios
        .post(getApiConstants().endpoints.REQUEST_TOKEN, {
          username: user,
          password,
          device_id: deviceId,
          device_version: appConfig.version,
          device_type: appConfig.clientType
        })
        .then((response) => {
          const { auth, data } = response.data;
          const {
            access_token: accessToken,
            expires_in: tokenExpiresIn,
            refresh_token: refreshToken
          } = auth;
          const {
            webrtc_username: webrtcUserName,
            webrtc_password: webrtcPassword,
            first_name: firstName,
            last_name: lastName,
            company_name: companyName,
            user_id: userId,
            account_id: accountId
          } = data;
          const responseParsed = {
            accessToken,
            tokenExpiresAt: new Date().getTime() + (tokenExpiresIn * 1000) - (30 * 1000),
            refreshToken,
            webrtcUserName,
            webrtcPassword,
            firstName,
            lastName,
            companyName,
            userId,
            accountId
          };
          resolve(responseParsed);
          return responseParsed;
        })
        .catch((error) => {
          const { response } = error;
          if (response) {
            const { data } = response;

            if (data) {
              const { message } = data;

              if (message) {
                reject(message);
              }
            }
          }

          reject(uiString.AUTH_ERROR);
        });
    });

    return dataPromise;
  };

  /**
   * Requests a logout action
   * requeres the user token to be set to succseed
   *
   * @returns a promise that can either succseed or fail
   * @returns a successfull promise returns the logout response
   * @returns a failed promise returns an error message
   */
  logout = () => {
   // const dataPromise = new Promise((resolve, reject) => {
   //   appConfig.axios
   //     .get(getApiConstants().endpoints.LOGOUT)
   //     .then((response) => {
   //       resolve(response);
   //       return response;
   //     })
   //     .catch((error) => {
   //       reject(error.response.statusText);
   //     });
   // });

   // return dataPromise;
   return Promise.resolve();
  };

  /**
   * Send the request to get the permissions for the current user
   *
   * @returns the userRole, userPermissions
   */
  getUserPermissions = () => {
    const dataPromise = new Promise((resolve, reject) => {
      const endpoint = getApiConstants().endpoints.USER_PERMISSIONS;
      appConfig.axios
        .get(endpoint)
        .then((response) => {
          const { data } = response.data;
          const userRole = data.user_role;
          const userPermissions = new UserPermissionsData({
            itemBlockedNumber: data.user_permissions.item_blocked_number,
            itemCallRecording: data.user_permissions.item_call_recording,
            itemDid: data.user_permissions.item_did_access,
            itemTextMessaging: data.user_permissions.item_text_messaging,
            pageCallHistory: data.user_permissions.page_call_history,
            pageVoicemail: data.user_permissions.page_voicemail,
          });
          const permissions = { userRole, userPermissions };

          resolve(permissions);
          return permissions;
        })
        .catch((error) => {
          if (error && error.response) {
            const errorParsed = new Error(error.response.statusText);
            reject(errorParsed);
          } else {
            reject(error);
          }
        });
    });

    return dataPromise;
  };

  // #endregion Authentication
  // #region Push Notifications
  /**
   * Request to register for the push notification service
   * Uses the machineIdSync() hash for the device_uuid
   *
   * @param deviceToken the notification service token for this device
   * @returns a promise that can either succseed or fail
   * @returns a successfull promise returns the service response
   * @returns a failed promise returns an error message
   */
  registerForNotifications = (deviceToken) => {
    const deviceId = appConfig.machineId;

    // get the device name from the hostname
    const deviceName = os.hostname();

    const dataPromise = new Promise((resolve, reject) => {
      appConfig.axios
        .post(getApiConstants().endpoints.REGISTER_NOTIFICATIONS(appConfig.clientType), {
          device_uuid: deviceId,
          device_token: deviceToken,
          device_name: deviceName,
        })
        .then((response) => {
          resolve(response);
          return response;
        })
        .catch((error) => {
          reject(error.response.statusText);
        });
    });

    return dataPromise;
  };

  // #endregion Push Notifications

  // #region Call History

  /**
   * Request Call History for the current user
   *
   * @returns an array containing CallHistoryData objects
   * or an empty array if there is no Call History
   */
  getCallHistory = (page?: number) => {
    const dataPromise = new Promise((resolve, reject) => {
      appConfig.axios
        .get(getApiConstants().endpoints.GET_CALL_HISTORY(page))
        .then((response) => {
          const { data, pagination } = response.data
          const parsedData = callHistoryJSONParser(data);
          resolve({calls: parsedData, pagination});
          return parsedData;
        })
        .catch((error) => {
          if (error && error.response) {
            const errorParsed = new Error(error.response.statusText);
            reject(errorParsed);
          } else {
            reject(error);
          }
        });
    });

    return dataPromise;
  };


  // #endregion Call History

  // #region Voicemail

  /**
   * @returns an array containing all voicemails
   */
  getVoicemails = async () => {
    try {
      const response = await appConfig.axios.get(getApiConstants().endpoints.voicemail.GET_VOICEMAILS);
      return response.data.data;
    } catch (error) {
      if (error && (error as any).response) {
        const errorParsed = new Error((error as any).response.statusText);
        throw ({
          originalError: error,
          errorParsed
        });
      } else {
       throw ({
          originalError: error,
          errorParsed: "Error can not be parsed"
        });
      }
    }
  };

  /**
   * @returns an array containing all voicemailBoxes
   */
  getVoicemailBoxes = () => {
    const dataPromise = new Promise((resolve, reject) => {
      appConfig.axios
        .get(getApiConstants().endpoints.voicemail.GET_VOICEMAIL_BOXES)
        .then((response) => {
          const parsedData = voicemailMailboxJSONParser(
            response.data.data
          );

          resolve(parsedData);
          return parsedData;
        })
        .catch((error) => {
          if (error && error.response) {
            const errorParsed = new Error(error.response.statusText);
            reject(errorParsed);
          } else {
            reject(error);
          }
        });
    });

    return dataPromise;
  };

  /**
   * Update the readStatus for a voicemail
   *
   * @param voicemailId the ID of the voicemail to update
   * @param readStatus the new read status
   * @returns success or failure
   */
  updateVoicemailReadStatus = (props) => {
    const { voicemailId, readStatus } = props;
    const dataPromise = new Promise((resolve, reject) => {
      const markAsReadUnread =
        readStatus === true
          ? getApiConstants().endpoints.voicemail.MARK_AS_READ
          : getApiConstants().endpoints.voicemail.MARK_AS_UNREAD;

      const endpoint = `${
        getApiConstants().endpoints.voicemail.VOICEMAIL_BASE
      }/${voicemailId}/${markAsReadUnread}`;
      appConfig.axios
        .patch(endpoint)
        .then((response) => {

          resolve(true);
          return null;
        })
        .catch((error) => {
          if (error && error.response) {
            const errorParsed = new Error(error.response.statusText);
            reject(errorParsed);
          } else {
            reject(error);
          }
        });
    });

    return dataPromise;
  };

  /**
   * Delete a voicemail with ID
   *
   * @param voicemailId the ID of the voicemail to delete
   * @returns success or failure
   */
  deleteVoicemail = (voicemailId) => {
    const dataPromise = new Promise((resolve, reject) => {
      const endpoint = `${
        getApiConstants().endpoints.voicemail.VOICEMAIL_BASE
      }/${voicemailId}`;
      appConfig.axios
        .delete(endpoint)
        .then((response) => {

          resolve(true);
          return true;
        })
        .catch((error) => {
          if (error && error.response) {
            const errorParsed = new Error(error.response.statusText);
            reject(errorParsed);
          } else {
            reject(error);
          }
        });
    });

    return dataPromise;
  };

  /**
   * Get the voicemail file for a voicemail ID
   *
   * @param voicemailId the ID of the voicemail file to get
   * @returns the absolute path to a Voicemail file
   */
  getVoicemailFile = (props) => {
    const { voicemailId } = props;
    const dataPromise = new Promise((resolve, reject) => {

      const endpoint = `${
        getApiConstants().endpoints.voicemail.VOICEMAIL_BASE
      }/${voicemailId}/${getApiConstants().endpoints.voicemail.AUDIO_URL}`;

      appConfig.axios
        .get(endpoint)
        .then((response) => {
          const { url } = response.data.data;

          this.downloadVoicemailFile(url, voicemailId)
            .then((filePath) => {
              resolve(filePath);
              return filePath;
            })
            .catch((error) => {
              reject(error);
            });

          return null;
        })
        .catch((error) => {
          if (error && error.response) {
            const errorParsed = new Error(error.response.statusText);
            reject(errorParsed);
          } else {
            reject(error);
          }
        });
    });

    return dataPromise;
  };

  /**
   * Download a voicemail file and store it in the user folder.
   * The file name is voicemailId.VOICEMAIL_FILE_EXTENSION
   *
   * @param url the remote URL of the voicemail file
   * @param voicemailId the id of the voicemail
   */
  downloadVoicemailFile = (url, voicemailId) => {
    
    const filePath = new FilePathHelper().getVoicemailFilePath();

    const dataPromise = new Promise((resolve, reject) => {

      // create a local instance that does not have the Authorization header
      let axiosLocalInstance = appConfig.axios.create();
      delete axiosLocalInstance.defaults.headers.common['Authorization'];
      axiosLocalInstance
        .get(url, {
          responseType: "arraybuffer",
        })
        .then((response) => {
          try {
            if (!appConfig.fileStorage?.exists(filePath)) {
              appConfig.fileStorage?.createPath(filePath);
            }

            const filePathWithFileName = new FilePathHelper().getVoicemailFilePathAndName(
              voicemailId
            );

            const { data } = response;

            appConfig.fileStorage?.save(
              filePathWithFileName,
              Buffer.from(new Uint8Array(data))
            );
            resolve(filePathWithFileName);
            return filePathWithFileName;
          } catch (error) {

            reject(error);
          }

          return null;
        })
        .catch((error) => {
          if (error && error.response) {
            const errorParsed = new Error(error.response.statusText);
            reject(errorParsed);
          } else {
            reject(error);
          }
        });
    });

    return dataPromise;
  };

  // #endregion Voicemail

  // #region Text Messages

  /**
   * Get all conversations
   *
   * @returns a conversation data or an error
   */
  getConversations = async (props) => {
    const { before, since, offset, firstMessageAfter, firstMessageBefore, talkrouteNumber, minMessagesCount, maxMessagesCount, pageSize } = props;
      let endpoint = getApiConstants().endpoints.conversations
        .CONVERSATIONS_BASE;
      const params = [
          this.buildURLParamSince(since),
          this.buildURLParamOffset(offset),
          this.buildURLParamBefore(before),
          this.buildURLParamsLimit(pageSize),
          this.buildURLParamFirstMessageBefore(firstMessageBefore),
          this.buildURLParamFirstMessageAfter(firstMessageAfter),
          this.buildURLParamTalkrouteNumber(talkrouteNumber),
          this.buildURLParamMinMessagesCount(minMessagesCount),
          this.buildURLParamMaxMessagesCount(maxMessagesCount),
      ].filter(param => param !== "").join("&");

      endpoint += params ? `?${params}` : "";
      try {
        const response = await appConfig.axios.get<{
          data: IConversationV1[];
          pagination: {
            count: number;
            limit: number;
            offset: number;
            timestamp: number;
          }
        }>(endpoint);
        const parsedData = conversationJSONParser(response.data.data);

        const { pagination } = response.data;
  
        return { conversations: parsedData, pagination };
      } catch (error) {
        if (error && (error as any).response) {
          const errorParsed = new Error((error as any).response.statusText);
         throw errorParsed;
        } else {
          throw error;
        }
      }
  };

  /**
   * Get messages from the Talkroute API
   *
   * @param conversationId the ID to get messages for
   * @param since the time stamp to get new messages since. Takes precedence over offset
   * @param offset start from this message number. Note that offset is not honored when since is present
   */
  getMessages = (props) => {
    const { since, conversationId, offset } = props;
    const dataPromise = new Promise((resolve, reject) => {
      let endpoint = `${
        getApiConstants().endpoints.messages.MESSAGES_BASE
      }/${conversationId}`;
      const sinceParam = this.buildURLParamSince(since);
      const offsetParam = this.buildURLParamOffset(offset);
      endpoint += sinceParam + offsetParam;

      appConfig.axios
        .get(endpoint)
        .then((response) => {
          const { data, pagination } = response.data
          const messages = textMessageJSONParser(data);

          resolve({ messages, pagination });
          return messages;
        })
        .catch((error) => {
          if (error && error.response) {
            const errorParsed = new Error(error.response.statusText);
            reject(errorParsed);
          } else {
            reject(error);
          }
        });
    });

    return dataPromise;
  };

    /**
   * Get messages CSV download from the Talkroute API
   *
   * @param conversationId the ID to get messages for
   */
    getMessagesDownload = (props) => {
      const { conversationId } = props;
      let endpoint = `${getApiConstants().endpoints.messages.MESSAGES_BASE}/${conversationId}`;

      return appConfig.axios
        .get(endpoint)
        .then((response) => {
          const export_link = response.data.export_link;

          return fetch(export_link, {
            method: 'GET',
            headers: {
              'Accept': 'text/csv',
            },
          })
          .then(response => response.blob())
          .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'conversation.csv';
            a.click();
            window.URL.revokeObjectURL(url);
          })
          .catch(error => console.error('Error:', error));
        })
        .catch((error) => {
          if (error && error.response) {
            const errorParsed = new Error(error.response.statusText);
            throw errorParsed;
          } else {
            throw error;
          }
        });
    };

  /**
   * Update the readStatus for a conversation
   *
   * @param conversationId the ID of the conversation to update
   * @param since the timestamp of the last conversation to mark as read
   * @returns success or failure
   */
  markConversationAsRead = (props) => {
    const { conversationId, since } = props;
    const dataPromise = new Promise((resolve, reject) => {
      let endpoint = `${
        getApiConstants().endpoints.messages.MESSAGES_BASE
      }/${conversationId}/${getApiConstants().endpoints.messages.MARK_AS_READ}`;
      const lastSeenParam = this.buildURLParamLastSeen(since);
      endpoint += lastSeenParam;

      appConfig.axios
        .post(endpoint)
        .then((response) => {

          resolve(true);
          return response;
        })
        .catch((error) => {
          if (error && error.response) {
            const errorParsed = new Error(error.response.statusText);
            reject(errorParsed);
          } else {
            reject(error);
          }
        });
    });

    return dataPromise;
  };

  /**
   * Delete a conversation with ID
   *
   * @param conversationId the ID of the voicemail to delete
   * @param since the timestamp of the last conversation to delete
   * @returns success or failure
   */
  deleteConversation = (params) => {
    const { conversationId, since } = params;
    const dataPromise = new Promise((resolve, reject) => {
      let endpoint = `${
        getApiConstants().endpoints.messages.MESSAGES_BASE
      }/${conversationId}`;
      const lastSeenParam = this.buildURLParamLastSeen(since);
      endpoint += lastSeenParam;

      appConfig.axios
        .delete(endpoint)
        .then((response) => {

          resolve(true);
          return response;
        })
        .catch((error) => {
          if (error && error.response) {
            const errorParsed = new Error(error.response.statusText);
            reject(errorParsed);
          } else {
            reject(error);
          }
        });
    });

    return dataPromise;
  };

  /**
   * Send a message
   *
   * @returns a copy of the message that was just sent
   */
  sendMessage = (props) => {
    const { messageBody, conversationId, files } = props;

    const dataPromise = new Promise(async (resolve, reject) => {

      const attachmentsData: any[] = [];

      if (validateArray(files)) {
        // If there are any files along with the message, process them
        files.forEach((file) => {
          const imageComponents = file.split(",");
          const imageData = imageComponents[1]
            ? imageComponents[1]
            : this.imageToBase64(imageComponents[0]);
          const nextAttachment = {
            attachment: imageData,
          };

          attachmentsData.push(nextAttachment);
        });
      }


      const endpoint = `${
        getApiConstants().endpoints.messages.MESSAGES_BASE
      }/${conversationId}`;
      const request: any = {};
      if (messageBody) {
        request.message_body = messageBody;
      }

      if (validateArray(attachmentsData)) {
        // If any files were processed, add them to the request
        request.attachments = attachmentsData;
      }

      appConfig.axios
        .post(endpoint, request)
        .then((response) => {
          const { data } = response.data;
          const messages = textMessageJSONParser(data);
          const message = messages.pop();

          resolve({ message });
          return message;
        })
        .catch((error) => {
          if (error && error.response) {
            const errorParsed = new Error(error.response.statusText);
            reject(errorParsed);
          } else {
            reject(error);
          }
        });
    });

    return dataPromise;
  };

  /**
   * Convert an image to base64
   *
   * @param imagePath the file path to the image
   * @returns a base64 string representing the image
   */
  imageToBase64 = (filePath) => {
    try {
      const bitmap = appConfig.fileStorage!.get(filePath);
      const buffer = Buffer.from(bitmap);
      const base64 = buffer.toString("base64");

      return base64;
    } catch (error) {

      return "";
    }
  };

  // #endregion Text Messages

  // #region Picture Messages

  /**
   * Get the file attachment for a message
   *
   * @param messageId the id of the message
   * @param attachmentId the id of the attachment
   * @param conversationId the id of the conversation
   */
  getAttachmentFile = (props) => {
    const { messageId, attachmentId, conversationId } = props;
    const dataPromise = new Promise((resolve, reject) => {

      const endpoint = `${
        getApiConstants().endpoints.messages.MESSAGE_ATTACHMENT_BASE
      }/${messageId}/${
        getApiConstants().endpoints.messages.MESSAGE_ATTACHMENT
      }/${attachmentId}`;

      appConfig.axios
        .get(endpoint, {
          responseType: "arraybuffer",
        })
        .then((response) => {

          const { data } = response;
          const uint8arrData = new Uint8Array(data)
          const signature = (uint8arrData).subarray(0, 4);
          const fileExtension = getExtensionFromSignature(signature)

          this.saveAttachmentFile({
            conversationId,
            attachmentId,
            fileExtension,
            data: Buffer.from(uint8arrData),
          })
            .then((filePath) => {
              resolve(filePath);
              return filePath;
            })
            .catch((error) => {
              reject(error);
            });
          return null;
        })
        .catch((error) => {
          if (error && error.response) {
            const errorParsed = new Error(error.response.statusText);
            reject(errorParsed);
          } else {
            reject(error);
          }
        });
    });

    return dataPromise;
  };

  /**
   * Save a file attachment to the local storage
   *
   * @param conversationId the id of the conversation
   * @param attachmentId the id of the attachment
   * @param contentType the content type of the attachment
   * @param data a data representation of the attachment file
   */
  saveAttachmentFile = (params) => {
    const dataPromise = new Promise((resolve, reject) => {
      const { conversationId, attachmentId, fileExtension, data } = params;

      try {
        const filePathWithFileName = new FilePathHelper().getMessageAttachmentFilePathAndName(
          {
            conversationId,
            attachmentId,
            fileExtension,
          }
        );
        appConfig.fileStorage?.save(filePathWithFileName, data);

        resolve(filePathWithFileName);
        return filePathWithFileName;
      } catch (error) {

        reject(error);
      }
    });

    return dataPromise;
  };

  // #endregion Picture Messages

  // #region Phone Numbers
  /**
   * Fetch the Talkroute phone numbers for the current user
   *
   * @returns the Talkroute phone numbers
   */
  getPhoneNumbers = () => {
    const dataPromise = new Promise((resolve, reject) => {
      const endpoint = getApiConstants().endpoints.PHONE_NUMBERS;
      appConfig.axios
        .get(endpoint)
        .then((response) => {
          const { data } = response.data
          const parsedData = phoneNumberJSONParser(
            data
          );


          resolve(parsedData);
          return parsedData;
        })
        .catch((error) => {
          if (error && error.response) {
            const errorParsed = new Error(error.response.statusText);
            reject({
              originalError: error,
              errorParsed
            });
          } else {
            reject({
              originalError: error,
              errorParsed: "Error can not be parsed"
            });
          }
        });
    });
    return dataPromise;
  };
  // #endregion Phone Numbers

  // #region WebRTC Connection Data

  /**
   * Fetch the WebRTC connection data. Currently a stub, until the service becomes available
   */
  fetchWebRTCConnectionData = () => {
    const dataPromise = new Promise((resolve) => {
      const webRTCConstants = getWebRTCConstants();
      const connectionData = {
        realm: webRTCConstants.connection.realm,
        impi: webRTCConstants.connection.impi,
        impu: webRTCConstants.connection.impu,
        password: webRTCConstants.connection.password,
        displayName: webRTCConstants.connection.displayName,
        webSocketProxyUrl: webRTCConstants.connection.websocketProxyUrl,
      };

      resolve(connectionData);
      // Error parsing to be used when the real API is available
      // const { response } = error;
      // let parsedError = new Error(response.statusText);
      // if (response.status === 409) {
      //   parsedError = new Error(uiString.MAX_NUMBER_LOGINS_REACHED);
      // }
      // reject(errorParsed)
    });
    return dataPromise;
  };

  // #endregion WebRTC Connection Data

  // #region URLParamBuilders
  buildURLParamSince = (since) =>
    since
      ? `${getApiConstants().since}${since}`
      : "";
  
  buildURLParamsLimit = (limit) => 
    limit
      ? `${getApiConstants().limit}${limit}`
      : "";

  buildURLParamBefore = (before) =>
    before
      ? `${getApiConstants().before}${before}`
      : "";

  buildURLParamFirstMessageBefore = (firstMessageBefore) => 
    firstMessageBefore
      ? `${getApiConstants().firstMessageBefore}${firstMessageBefore}`
      : "";

  buildURLParamFirstMessageAfter = (firstMessageAfter) =>
    firstMessageAfter
      ? `${getApiConstants().firstMessageAfter}${firstMessageAfter}`
      : "";

  buildURLParamTalkrouteNumber = (talkrouteNumber) =>
    talkrouteNumber
      ? `${getApiConstants().talkrouteNumber}${talkrouteNumber}`
      : "";

  buildURLParamMinMessagesCount = (minMessagesCount) =>
    minMessagesCount
      ? `${getApiConstants().minMessagesCount}${minMessagesCount}`
      : "";

  buildURLParamMaxMessagesCount = (maxMessagesCount) =>
    maxMessagesCount
      ? `${getApiConstants().maxMessagesCount}${maxMessagesCount}`
      : "";

  buildURLParamOffset = (offset) =>
    offset
      ? `${getApiConstants().offset}${offset}`
      : "";

  buildURLParamLastSeen = (lastSeen) =>
    lastSeen
      ? `${getApiConstants().quaryParam}${
          getApiConstants().lastSeenCreatedAtMicroSeconds
        }${lastSeen}`
      : "";
  // #endregion URLParamBuilders

  // #region Block Call
  /**
   * Send the request to fetch the blocked phone numbers for the current user
   *
   * @returns the blocked phone numbers
   */
  getBlockedNumbers = () => {
    const dataPromise = new Promise((resolve, reject) => {
      const endpoint = getApiConstants().endpoints.BLOCK_CALL;
      appConfig.axios
        .get(endpoint)
        .then((response) => {
          const {data: blockedNumbers} = response.data
          resolve(blockedNumbers);
          return blockedNumbers;
        })
        .catch((error) => {
          if (error && error.response) {
            const errorParsed = new Error(error.response.statusText);
            reject({
              originalError: error,
              errorParsed
            });
          } else {
            reject({
              originalError: error,
              errorParsed: "Error can not be parsed"
            });
          }
        });
    });

    return dataPromise;
  };

  /**
   * Send the request to block the call for the phone number
   *
   * @param number the number which will be blocked
   * @returns number, enabled, id, created_at, updated_at
   */
  blockNumber = (number) => {
    const dataPromise = new Promise((resolve, reject) => {
      const endpoint = getApiConstants().endpoints.BLOCK_CALL;
      appConfig.axios
        .post(endpoint, { number })
        .then((response) => {
          const { enabled } = response.data.data;
          resolve(enabled);
          return enabled;
        })
        .catch((error) => {
          const errorParsed = new Error(error.response?.statusText);
          reject(error);
        });
    });

    return dataPromise;
  };

  /**
   * Send the request to unblock the call for the phone number
   *
   * @param number the number which will be unblocked
   */
  unblockNumber = (number) => {
    const dataPromise = new Promise((resolve, reject) => {
      const endpoint = getApiConstants().endpoints.BLOCK_CALL;
      appConfig.axios
        .delete(endpoint, { data: { number } })
        .then((response) => {

          resolve(true);
          return true;
        })
        .catch((error) => {
          reject(error);
          // if (error && error.response && error.response.status !== 403) {
          //   const errorParsed = new Error(error.response.statusText);
          //   reject(errorParsed);
          // } else {
          // }
        });
    });

    return dataPromise;
  };

  // #endregion Block Call

  /**
   * Return the axios interceptor
   * @returns any
   */
  getInterceptor = () => this.interceptor
}
const apiServiceInstance = new TalkrouteAPIService();

export default apiServiceInstance;
